import {Button, Icon, Dialog, Loading} from 'startlibs/lib/components'
import { fetchConnect, withRouteTo, withToggles } from 'startlibs/lib/hocs';
import {getFetcher, postFetcher} from 'startlibs'
import {ice} from 'icepath'
import {
  withForm,
  Errors,
  SimpleCheckbox,
  TextInput,
  Field
} from 'startlibs/lib/form';
import React from 'react'
import URLSearchParams from 'url-search-params'
import styled from 'styled-components';

import {Timer} from 'report-components/src/components/timer'

import {
  CheckoutPage,
  CouponFeedback,
  OrderSummaryWrapper,
  PlanBillingOptionContainer,
  PlanHeader,
  PlanPrice,
  PlanPriceDetail,
  PlanRadio,
  PromoCodeContainer,
  SubscriptionPlan,
  SubscriptionPlanList,
  TextInputWithButton,
  CurrentPlanLabel,
  InfoBox
} from './CheckoutComponents';
import {CheckoutSignupDialog} from './CheckoutSignupDialog'
import {CreditCardCheckout} from './CreditCardCheckout'
import {NewUserDialog} from '../user/ProfileData'
import {PLAN_TYPE_DETAILS} from '../user/Billing'
import {ReportCheckoutSuccess} from './CheckoutSuccess'
import {buildValidation} from '../lib/validation'
import {
  getAuthorities,
  getPlanType,
  normalizeSubscription,
  stripeIntervalToAbbreviation,
  stripeIntervalToInterval,
  toPrice
} from '../lib/index'
import {setNotifications, userSubscriptionSuccess} from '../reducers/index'

const PLAN_TYPE_HEADER = {
  'REPORT_BASIC': 'Basic',
  'REPORT_BASIC_V2': 'Free',
  'REPORT_BASIC_V3': 'Basic',
  'REPORT_STANDARD': 'Standard',
  'REPORT_STANDARD_V2': 'Standard',
  'REPORT_PRO': 'Pro'
}

const PriceChangeNotice = styled(InfoBox)`
  margin-top: -1rem;
  margin-bottom: 2.5rem;
  display: flex;
  align-items: center;
  font-size: 13px;
  ${Icon} {
    font-size: 24px;
    margin-right: 1rem;
    font-weight: 400;
  }
  h4 {
    margin: 0;
    font-size: 14px;
  }
`

const PlanRadioComponent = ({form, plan, monthlyCost, isCurrent}) =>
  <PlanRadio form={form}
             className="plan-box selectable"
             path="stripePlanId"
             discontinuedPlan={plan.discontinued}
             fieldValue={plan.id}>
    {plan.amount === 0 ? <PlanPrice>Free</PlanPrice> :
     <PlanPrice>${toPrice(plan.amount)} USD / {plan.interval}</PlanPrice>}
    {monthlyCost && <PlanPriceDetail>You save ${toPrice((monthlyCost * 12) - plan.amount)} USD</PlanPriceDetail>}
    {isCurrent && <CurrentPlanLabel>Current</CurrentPlanLabel>}
  </PlanRadio>


const SubscriptionPlansCard = ({form, plans, planType, currentPlanId, currentStatus, children}) => {
  const isCardSelected = plans.find(plan => plan.id === form.properties.stripePlanId)
  const discontinuedPlan = !plans.find(_ => !_.discontinued)
  return <SubscriptionPlan
    onClick={() => !isCardSelected && form.connector.setValue('stripePlanId', plans[0].id)}
    selected={!!isCardSelected}
    discontinuedPlan={discontinuedPlan}
  >
    <PlanHeader>{PLAN_TYPE_HEADER[planType]} {discontinuedPlan && '(discontinued)'}</PlanHeader>
    {PLAN_TYPE_DETAILS[planType]()}
    <PlanBillingOptionContainer>
      {plans.sort((a, b) => a.amount < b.amount ? 1 : -1).map(plan => <PlanRadioComponent
          plan={plan}
          key={plan.id}
          form={form}
          isCurrent={plan.id === currentPlanId && currentStatus !== 'trialing'}
          monthlyCost={plan.interval === 'year' && plans.filter(_ => _.interval !== 'year').map(_ => _.amount)[0]}
        />
      )}
    </PlanBillingOptionContainer>
  </SubscriptionPlan>
}

const PLAN_PARAMS_TRANSLATION = {
  'STANDARD': 'REPORT_STANDARD_V2',
  'PRO': 'REPORT_PRO',
  'BASIC': 'REPORT_BASIC_V3'
}


@fetchConnect(({user}) => ({user}), {userSubscriptionSuccess, setNotifications}, 'stripe')
@withForm()
@withRouteTo
@withToggles('couponLoading', 'isLoading', 'checkoutStep', 'firstTimeDialog')
export class ReportCheckout extends React.PureComponent {

  state = {}

  stripePlans = this.props.stripe.stripePlans.plans
  replacedPlans = this.props.stripe.replacedPlans ? this.props.stripe.replacedPlans.plans : []
  allPlans = [...this.stripePlans,...this.replacedPlans]

  isUpgradePlan = (new URLSearchParams(this.props.location.search)).get('of') === 'plan'
  isFirstTime = (new URLSearchParams(this.props.location.search)).get('from') === 'signup'

  preValidation = buildValidation({
    acceptedTerms: (v) => !v && 'You must read & agree to the License Terms.'
  })

  componentDidMount() {
    const params = new URLSearchParams(this.props.location.search)
    const planName = PLAN_PARAMS_TRANSLATION[(params.get('plan') || '').toUpperCase()] || 'REPORT_STANDARD_V2'
    const interval = (params.get('interval') || 'month')
    this.props.form.connector
      .setValue(
        'stripePlanId',
        (this.isFirstTime && this.stripePlans.filter(_ => _.interval === interval && _.name === planName).map(_ => _.id)[0]) ||
        (this.isUpgradePlan && this.stripePlans.filter(_ => _.interval === interval && _.name === 'REPORT_PRO').map(_ => _.id)[0]) ||
        (
          ice.get(this.state, ['currentSubscription', 'plan', 'id']) ||
          (
            this.stripePlans.find(_ => _.interval === interval && _.name === planName) ||
            this.stripePlans[0]
          ).id
        )
      )
    if (this.props.stripe.customer) {
      this.setStripeCustomer(this.props.stripe)
    } else if (this.props.user.id) {
      this.props.routeTo('/billing')
    }
    if (this.isFirstTime) {
      this.props.firstTimeDialog.open()
    }
  }

  componentDidUpdate(oldProps) {
    if (
      this.state.currentSubscription &&
      oldProps.form.properties.stripePlanId !== this.props.form.properties.stripePlanId
    ) {
      this.fetchProrate()
    }
  }


  setStripeCustomer = (stripe, fromSignup) => {
    const customer = stripe && stripe.customer
    if (customer) {
      const currentCard = customer.sources && customer.sources.data && customer.sources.data[0]
      const currentSubscription = normalizeSubscription(customer.subscriptions && customer.subscriptions.data && customer.subscriptions.data.find(_ => _.metadata && (!_.metadata.type || _.metadata.type === 'REPORT')), currentCard)
      this.setState({
        loadedStripeCustomer: true,
        currentCard,
        currentSubscription,
        fromSignup
      }, this.fetchProrate)
    } else {
      this.setState({
        loadedStripeCustomer: true,
        fromSignup
      })
    }
  }

  confirmCoupon = (e) => {
    e.preventDefault()
    const {stripePlanId, coupon} = this.props.form.properties
    this.props.couponLoading.open()
    getFetcher('/api/coupon-test', {coupon})
      .then((coupon) => this.fetchProrate(true, coupon))
      .catch(() => this.fetchProrate(true, {"id":coupon}))
  }

  isCanceled = () => this.state.currentSubscription && (this.state.currentSubscription.status === 'canceled' || this.state.currentSubscription.status === 'canceled_renewal')

  getCurrentPlanId = () => (this.isFirstTime || this.state.isCanceled) ? '' : ice.get(this.state, ['currentSubscription', 'plan', 'id'])

  hasCurrentActivePlan = () => !this.isFirstTime && this.state.currentSubscription && this.state.currentSubscription.status === 'active'
  isCurrentPlanSelected = () => this.hasCurrentActivePlan() && this.getCurrentPlanId() === this.props.form.properties.stripePlanId

  fetchProrate = (checkingCoupon, checkedCoupon) => {
    const currentPlan = this.getCurrentPlanId()
    if (!currentPlan) {return}
    const stripePlanId = this.props.form.properties.stripePlanId
    if (!this.state.currentSubscription || (this.hasCurrentActivePlan() && currentPlan === stripePlanId)) {
      this.props.couponLoading.close()
      if (checkingCoupon) {
        this.props.form.connector.setValue('coupon', '')
        const valid = checkedCoupon && checkedCoupon.valid
        this.setState({
          coupon: checkedCoupon,
          couponError: !valid && 'invalid.coupon'
        })
      }
      return
    }
    if (!checkingCoupon) {
      this.setState({prorate: null, couponError: null})
    }
    const coupon = checkingCoupon ? checkedCoupon : this.state.coupon
    getFetcher('/api/pro-rata', {
      subscriptionId: this.state.currentSubscription.id,
      stripePlanId,
      ...(coupon ? {coupon:coupon.id} : {})
    }).then(prorate => {
        if (checkingCoupon) {
          this.props.form.connector.setValue('coupon', '')
        }
        const couponError =
          checkingCoupon && !coupon
          ? 'invalid.coupon'
          : coupon && !prorate.discount
            ? 'invalid.coupon.plan'
            : prorate.discount
              ? null
              : this.state.couponError
        this.props.couponLoading.close()
        this.setState({
          prorate,
          coupon,
          couponError
        })
      })
      .catch(() => {
        this.props.couponLoading.close()
        if (checkingCoupon) {
          this.props.form.connector.setValue('coupon', '')
        }
        this.setState({prorate: null})
      })
  }

  isFreePlanSelected = () => {
    // const freePlanId = this.stripePlans.filter(_ => _.name === 'REPORT_BASIC_V2').map(_ => _.id)[0]
    // return this.props.form.properties.stripePlanId === freePlanId
    return false
  }

  goToCreditCardCheckout = () => {
    if (this.isFreePlanSelected()) {
      //always false
      // this.signUpForFree()
    } else {
      this.props.checkoutStep.open(2)
    }
  }

  login = () => {
    const validated = this.preValidation(this.props.form.properties, {})
    if (validated.errors) {
      return this.props.form.connector.setErrors(validated.errors)
    }
    if (this.props.user.id) {
      if (this.isFreePlanSelected()) {
        //always false
        // this.props.isLoading.open()
        // this.signUpForFree()
      } else {
        this.props.checkoutStep.open(2)
      }
    } else {
      this.props.checkoutStep.open(1)
    }
  }

  /*signUpForFree = () => postFetcher('/api/update-subscription', {
    stripePlanId: this.props.form.properties.stripePlanId,
    subscriptionType: 'STORAGE'
  }, {method: 'PUT'}).then(() =>
    this.props.checkoutStep.open(3)
  )*/

  creditCardSuccess =() => {
    this.props.checkoutStep.open(3)
    const selectedPlanInfo = this.stripePlans.find(_ => _.id === this.props.form.properties.stripePlanId)
    this.props.userSubscriptionSuccess(getAuthorities(selectedPlanInfo.name))
  }

  render() {
    if (!this.isUpgradePlan && this.props.user.id && !this.state.loadedStripeCustomer) {
      return <Loading absolute size={40} borderWidth={6}/>
    }

    const {form, checkoutStep, firstTimeDialog, couponLoading, isLoading, user} = this.props
    const {coupon: appliedCoupon, prorate, couponError, fromSignup} = this.state

    const isCurrentPlanSelected = this.isCurrentPlanSelected()
    const hasCurrentActivePlan = this.hasCurrentActivePlan()

    const currentPlanInfo = this.allPlans.find(_ => _.id === this.getCurrentPlanId())
    const selectedPlanInfo = this.stripePlans.find(_ => _.id === form.properties.stripePlanId)


    const orderPreview = (changePlanButton, finished) => <OrderSummary
      currentPlanId={this.getCurrentPlanId()}
      form={form}
      finished={finished}
      changePlanButton={changePlanButton}
      confirmCoupon={this.confirmCoupon}
      couponLoading={couponLoading}
      couponError={couponError}
      prorate={prorate}
      appliedCoupon={appliedCoupon}
      plan={selectedPlanInfo}
    />

    if (checkoutStep.isOpen === 2) {
      return <CreditCardCheckout
        currentCard={this.state.currentCard}
        orderPreview={orderPreview}
        finalPlan={selectedPlanInfo}
        coupon={appliedCoupon && appliedCoupon.id}
        prorate={prorate}
        skipContactInfo
        workstationsVPN={0}
        form={form}
        hasCurrentActivePlan={hasCurrentActivePlan}
        isCurrentPlanSelected={isCurrentPlanSelected}
        nextStep={this.creditCardSuccess}
        prevStep={checkoutStep.close}
      />
    }
    if (checkoutStep.isOpen === 3) {
      return <ReportCheckoutSuccess
        isFreePlanSelected={this.isFreePlanSelected()}
        planName={getPlanType(selectedPlanInfo.name)}
        orderPreview={orderPreview}
        finishButton={<Button.Link highlight
                                   to="/">{fromSignup ? 'Start using Horos Cloud' : 'Go to home screen'}</Button.Link>}
      />
    }

    return <CheckoutPage className="content edit-profile">
      <div className={'profile-header checkout-header' + (hasCurrentActivePlan ? ' mobile-margin' : '')}>
        <h1>{hasCurrentActivePlan ? 'Update Horos Cloud subscription' : 'Horos Cloud subscription'}</h1>
        <Button.a small href='https://info.horosproject.org/horos-cloud-faq' target="_blank">Learn more</Button.a>
      </div>
      <h3>{hasCurrentActivePlan ? 'Select a new Horos Cloud Reporting plan' : 'Select your Horos Cloud Reporting plan'}</h3>
      {
        hasCurrentActivePlan && !currentPlanInfo.discontinued &&
        <p>Your current plan
          is <strong>{getPlanType(currentPlanInfo.name)} - {stripeIntervalToInterval(currentPlanInfo.interval)} billing
            (${toPrice(currentPlanInfo.amount)} USD / {stripeIntervalToAbbreviation(currentPlanInfo.interval)})</strong>.
        </p>
      }
      {
        hasCurrentActivePlan && currentPlanInfo.discontinued &&
        <div>
          <p>Your current plan has been discontinued:</p>
          <SubscriptionPlansCard
            planType={currentPlanInfo.name}
            form={form}
            plans={[currentPlanInfo]}
            currentPlanId={this.getCurrentPlanId()}
            currentStatus={this.state.currentSubscription.status}
          />
          <p className="warning-text"><b>You can keep your current plan and price or select a new plan and get
            access to
            new features:</b></p>
        </div>
      }
      <PlanTypeSelection
        form={form}
        currentPlanId={this.getCurrentPlanId()}
        currentStatus={ice.get(this.state, 'currentSubscription', 'status')}
        currentInterval={ice.get(this.state, ['currentSubscription', 'plan', 'interval'])}
        stripePlans={this.stripePlans}
      />
      {
        isCurrentPlanSelected &&
        <InfoBox>
          <span><strong>The plan and billing period you selected is already active in your current subscription.</strong> If you'd like to change your plan, select another option above and click 'Update subscription'. For changing your credit card details only, click below.</span>
        </InfoBox>
      }
      {orderPreview()}
      <div className="checkbox-list">
        <SimpleCheckbox
          form={form}
          label={<span className="license-terms-agreement">By continuing, you confirm you have read and accept the Horos Cloud <a
            className="link nowrap" href="/license" target="_blank">License Terms</a>.</span>}
          path="acceptedTerms"
          tabIndex={1}
        />
      </div>
      <Errors form={form}/>
      <div className="profile-footer">
        {user.id && <Button.Link to="/billing">Cancel</Button.Link>}
        <Button
          highlight
          isLoading={isLoading.isOpen}
          disabled={isCurrentPlanSelected}
          onClick={this.login}

        >
          {this.isFreePlanSelected() && !isCurrentPlanSelected ? 'Subscribe' : 'Next'}
        </Button>
        {
          checkoutStep.isOpen === 1 &&
          <CheckoutSignupDialog
            closeDialog={checkoutStep.close}
            form={form}
            setStripCustomer={this.setStripeCustomer}
            nextStep={this.goToCreditCardCheckout}
          />
        }
      </div>
      {firstTimeDialog.isOpen && <FirstTimeDialog closeDialog={firstTimeDialog.close}/>}
    </CheckoutPage>
  }

}

const PlanTypeSelection = ({form, stripePlans, currentStatus, currentPlanId, currentInterval}) => {
  const availablePlans = currentInterval === 'year' ? stripePlans : stripePlans.filter(_ => _.interval !== 'year')
  return <SubscriptionPlanList>
    <SubscriptionPlansCard
      planType="REPORT_BASIC_V3"
      form={form}
      plans={availablePlans.filter(_ => _.name === 'REPORT_BASIC_V3')}
      currentPlanId={currentPlanId}
      currentStatus={currentStatus}
    />
    <SubscriptionPlansCard
      planType="REPORT_STANDARD_V2"
      form={form}
      plans={availablePlans.filter(_ => _.name === 'REPORT_STANDARD_V2')}
      currentPlanId={currentPlanId}
      currentStatus={currentStatus}
    />
    <SubscriptionPlansCard
      planType="REPORT_PRO"
      form={form}
      plans={availablePlans.filter(_ => _.name === 'REPORT_PRO')}
      currentPlanId={currentPlanId}
      currentStatus={currentStatus}
    />
  </SubscriptionPlanList>
}

const OrderSummary = ({changePlanButton, finished, currentPlanId, plan, form, confirmCoupon, appliedCoupon, couponLoading, prorate, couponError}) => {
  if (currentPlanId === form.properties.stripePlanId || !plan || plan.amount === 0) return null
  const nonNullProrate = prorate || {}
  const saldo = nonNullProrate.total + nonNullProrate.startingBalance
  const intervals = saldo <= 0 ? -saldo / plan.amount : 0
  const nextChargeDate = new Date(nonNullProrate.nextPaymentAttempt * 1000)
  if (plan.interval === 'year') {
    nextChargeDate.setFullYear(nextChargeDate.getFullYear() + Math.ceil(intervals))
  } else {
    nextChargeDate.setMonth(nextChargeDate.getMonth() + Math.ceil(intervals))
  }
  const nextCharge = intervals === 0 ? nonNullProrate.amountDue : plan.amount + (saldo % plan.amount)

  const validCoupon = prorate ? nonNullProrate.discount && nonNullProrate.discount.coupon : (appliedCoupon && appliedCoupon.valid ? appliedCoupon : null)

  const couponDiscount =
    validCoupon ?
    (validCoupon.amountOff ? validCoupon.amountOff : Math.round(plan.amount * validCoupon.percentOff / 100)) :
    0

  const credit = plan.amount - saldo + (isNaN(couponDiscount) ? 0 : -couponDiscount)

  return <OrderSummaryWrapper>
    <div className="heading-text">
      <h3>Your Order {changePlanButton}</h3>
    </div>
    <div className="card order">
      <div className="order-table">
        <div><span><b>{PLAN_TYPE_HEADER[plan.name]} Plan</b> ({stripeIntervalToInterval(plan.interval)})</span>
          <span className="amount"><b>${toPrice(plan.amount)} USD</b></span></div>
        {
          validCoupon &&
          <div>
            <span>Promo code discount ({validCoupon.percentOff ? `${validCoupon.percentOff}%` : `$${toPrice(validCoupon.amountOf)} USD`}):</span>
            <span className="discount amount">-${toPrice(couponDiscount)} USD</span>
          </div>
        }
        {
          credit > 0 &&
          <div>
            <span>Credit from current subscription:</span>
            <span className="discount amount">-${toPrice(credit)} USD</span>
          </div>
        }
        <div className="total">
          <span><b>Your first payment{!isNaN(nextChargeDate.getTime()) &&
          <span className="nowrap"> (on <Timer key="date" language="en" dateFormat="L dd, yyyy" noPrefix noHours
                                               time={nextChargeDate}/>)</span>}:</b></span>
          <span className="amount"><b>${toPrice(isNaN(nextCharge) ? plan.amount : nextCharge)} USD</b></span>
        </div>
      </div>
      {
        !finished && <PromoCodeContainer>
          <form onSubmit={confirmCoupon}>
            <Field label="Promo Code">
              <TextInputWithButton>
                <div className="input-container">
                  <TextInput form={form} path="coupon" placeholder="Insert your promo code"/>
                </div>
                <Button small isLoading={couponLoading.isOpen} type="submit">Apply</Button>
              </TextInputWithButton>
            </Field>
          </form>
          {
            validCoupon &&
            <CouponFeedback success>
              <span className="applied-coupon">{appliedCoupon.id}</span>
              <b>Coupon successfully applied.</b>
              <div>
                {validCoupon.percentOff ?
                 `${validCoupon.percentOff}% off on first payment.` :
                 `$${toPrice(validCoupon.amountOf)} USD off.`
                }
              </div>
            </CouponFeedback>
          }
          {
            couponError &&
            <CouponFeedback error>
              {appliedCoupon && <span className="applied-coupon">{appliedCoupon.id}</span>}
              {couponError === 'invalid.coupon' && <div>Sorry, the coupon you entered is not valid.</div>}
              {couponError === 'invalid.coupon.plan' &&
              <div>Sorry, coupon not valid for this user account or selected plan.</div>}
            </CouponFeedback>
          }
        </PromoCodeContainer>
      }
      {
        finished &&
        <PromoCodeContainer>
          {(PLAN_TYPE_DETAILS[plan.name] || (() => ''))()}
        </PromoCodeContainer>
      }
    </div>
  </OrderSummaryWrapper>
}

const FirstTimeDialog = ({closeDialog}) =>
  <NewUserDialog
    title="Welcome to Horos Cloud"
  >
    <Dialog.Content>
      <img src="/public/favicon.png" alt="Horos Icon"/>
      <p>
        Check our subscription plans and select<br/> one that best fits your practice.
      </p>
    </Dialog.Content>
    <Dialog.Footer>
      <Button highlight onClick={closeDialog}>Continue</Button>
    </Dialog.Footer>
  </NewUserDialog>



// WEBPACK FOOTER //
// ./src/javascripts/checkout/ReportCheckout.jsx